import { useEffect, useState, useCallback, useMemo, useRef, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  getProductos,
  crearProductoMasivo,
  cambiarEstadoProductosMasivo,
  cambiarDestacadoProductosMasivo,
} from "../../../services/usuarios_tienda/productos";
import { IconEdit } from "../../../icons/Icons";
import InventoryLoadModal from "./InventoryLoadModal";
import InventoryAdjustModal from "./InventoryAdjustModal";
import { IconPlus, IconSettings } from "../../../icons/Icons";
import InventoryHistoryModal from "./InventoryHistoryModal";
import { IconHistory } from "../../../icons/Icons";
import { hasPerm } from "../../../utils/hasPerm";
import ModalResultadoCargaMasiva from "./ModalResultadoCargaMasiva";
import { obtenerConfiguracionTiendaService } from "../../../services/usuarios_tienda/ConfiguracionTienda";
import { formatearPrecio } from "../../../utils/moneda";
import { fileURL } from "../../../utils/fileURL";
import { getProductSku } from "../../../utils/slugifySkuPreview";
import "./ProductsPage.css";

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500];
const VISIBLE_PAGE_COUNT = 3;
const FILTER_MENU_MAX_HEIGHT = 280;

const PRODUCT_STATUS_FILTER_OPTIONS = [
  { key: "ACTIVO", label: "Activos", tone: "activo" },
  { key: "INACTIVO", label: "Inactivos", tone: "inactivo" },
  { key: "SIN_STOCK", label: "Sin stock", tone: "nostock" },
  { key: "ALL", label: "Todos", tone: "all" },
];

function productMatchesStatusFilter(product, filter) {
  if (filter === "ALL") return true;
  if (filter === "ACTIVO") return Number(product.estado) === 1;
  if (filter === "INACTIVO") return Number(product.estado) !== 1;
  if (filter === "SIN_STOCK") return Number(product.cantidad_disponible ?? 0) <= 0;
  return true;
}

function ProductsPage() {
  const navigate = useNavigate();
  const [productos, setProductos] = useState([]);
  const [search, setSearch] = useState("");

  const [categoriaId, setCategoriaId] = useState("");
  const [marcaId, setMarcaId] = useState("");
  const [statusFilter, setStatusFilter] = useState("ACTIVO");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showLoadInventory, setShowLoadInventory] = useState(false);
  const [showAdjustInventory, setShowAdjustInventory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkModalView, setBulkModalView] = useState("result");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkError, setBulkError] = useState(null);
  const bulkCsvInputRef = useRef(null);
  const importMenuRef = useRef(null);

  const [importMenuOpen, setImportMenuOpen] = useState(false);
  const [openFilterDropdown, setOpenFilterDropdown] = useState(null);
  const [configuracionTienda, setConfiguracionTienda] = useState(null);

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState(null);

  const plantillaCsvHref = `${process.env.PUBLIC_URL || ""}/plantilla_productos_carga_masiva.csv`;

  const moneyQ = useCallback(
    (v) =>
      formatearPrecio(v, configuracionTienda, { espacioEntreSimbolo: true }) ||
      "—",
    [configuracionTienda]
  );

  const cargarProductos = useCallback(async () => {
    try {
      const data = await getProductos();
      setProductos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error cargando productos:", error);
      setProductos([]);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    obtenerConfiguracionTiendaService().then((res) => {
      if (!alive) return;
      if (res.ok && res.data) setConfiguracionTienda(res.data);
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    cargarProductos();
  }, [cargarProductos]);

  useEffect(() => {
    if (!importMenuOpen) return;
    const onDocMouseDown = (e) => {
      if (importMenuRef.current && !importMenuRef.current.contains(e.target)) {
        setImportMenuOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setImportMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [importMenuOpen]);

  useEffect(() => {
    if (openFilterDropdown == null) return;
    const close = () => setOpenFilterDropdown(null);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [openFilterDropdown]);

  const categorias = useMemo(() => {
    const map = new Map();
    productos.forEach((p) => {
      const id = p.categoria_producto_id;
      if (id != null) {
        map.set(String(id), p.categoria_nombre || `#${id}`);
      }
    });

    return [...map.entries()]
      .map(([id, nombre]) => ({ id, nombre }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [productos]);

  const marcas = useMemo(() => {
    const map = new Map();
    productos.forEach((p) => {
      const id = p.marca_producto_id;
      if (id != null) {
        map.set(String(id), p.marca_nombre || `#${id}`);
      }
    });

    return [...map.entries()]
      .map(([id, nombre]) => ({ id, nombre }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [productos]);

  const categoriaOptions = useMemo(
    () => [
      { value: "", label: "Todas las categorías" },
      ...categorias.map((c) => ({ value: c.id, label: c.nombre })),
    ],
    [categorias]
  );

  const marcaOptions = useMemo(
    () => [
      {
        value: "",
        label: marcas.length === 0 ? "Sin marcas" : "Todas las marcas",
      },
      ...marcas.map((m) => ({ value: m.id, label: m.nombre })),
    ],
    [marcas]
  );

  const productStats = useMemo(() => {
    let activos = 0;
    let inactivos = 0;
    let sinStock = 0;

    productos.forEach((p) => {
      if (Number(p.estado) === 1) activos += 1;
      else inactivos += 1;
      if (Number(p.cantidad_disponible ?? 0) <= 0) sinStock += 1;
    });

    return { activos, inactivos, sinStock, total: productos.length };
  }, [productos]);

  const hasActiveFilters = Boolean(
    search.trim() || categoriaId || marcaId || statusFilter !== "ACTIVO"
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    return productos.filter((p) => {
      const nombre = (p.nombre || "").toLowerCase();
      const sku = (getProductSku(p) || "").toLowerCase();

      const matchesSearch =
        !q || nombre.includes(q) || sku.includes(q);

      const matchesCat =
        !categoriaId || String(p.categoria_producto_id) === String(categoriaId);

      const matchesBrand =
        !marcaId || String(p.marca_producto_id) === String(marcaId);

      const matchesStatus = productMatchesStatusFilter(p, statusFilter);

      return matchesSearch && matchesCat && matchesBrand && matchesStatus;
    });
  }, [productos, search, categoriaId, marcaId, statusFilter]);

  const clearFilters = () => {
    setSearch("");
    setCategoriaId("");
    setMarcaId("");
    setStatusFilter("ACTIVO");
  };

  useEffect(() => {
    setTotalPages(Math.max(1, Math.ceil(filtered.length / pageSize)));
    setPage(1);
  }, [filtered.length, pageSize]);

  const start = (page - 1) * pageSize;
  const paginated = filtered.slice(start, start + pageSize);

  const paginatedIds = paginated.map((p) => p.producto_id);
  const allPageSelected =
    paginatedIds.length > 0 && paginatedIds.every((id) => selectedIds.has(id));
  const somePageSelected = paginatedIds.some((id) => selectedIds.has(id));

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) paginatedIds.forEach((id) => next.delete(id));
      else paginatedIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const selectedProducts = productos.filter((p) => selectedIds.has(p.producto_id));
  const hasInactiveSelected = selectedProducts.some((p) => Number(p.estado) !== 1);
  const hasActiveSelected = selectedProducts.some((p) => Number(p.estado) === 1);

  // El destacado masivo solo aplica a productos activos seleccionados.
  const activeSelectedProducts = selectedProducts.filter(
    (p) => Number(p.estado) === 1
  );
  const allActiveSelectedDestacados =
    activeSelectedProducts.length > 0 &&
    activeSelectedProducts.every((p) => Number(p.destacado) === 1);

  const handleBulkEstado = async (estado) => {
    if (bulkBusy) return;

    // Solo afecta a los productos cuyo estado realmente cambia (evita reactivar
    // los ya activos o desactivar los ya inactivos).
    const ids = selectedProducts
      .filter((p) => Number(p.estado) !== Number(estado))
      .map((p) => p.producto_id);

    if (ids.length === 0) return;

    setBulkBusy(true);
    setBulkMsg(null);
    try {
      const { ok, fail } = await cambiarEstadoProductosMasivo(ids, estado);
      await cargarProductos();
      setSelectedIds(new Set());
      const accion = estado === 1 ? "activados" : "inactivados";
      setBulkMsg({
        type: fail ? "error" : "success",
        text: fail
          ? `${ok} producto(s) ${accion}, ${fail} con error.`
          : `${ok} producto(s) ${accion} correctamente.`,
      });
    } catch (e) {
      console.error("Error en acción masiva:", e);
      setBulkMsg({ type: "error", text: "No se pudo completar la acción masiva." });
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkDestacado = async () => {
    if (bulkBusy) return;

    // Si todos los activos seleccionados ya están destacados, el botón los quita;
    // si no, los marca. Solo se envían los que realmente cambian.
    const target = allActiveSelectedDestacados ? 0 : 1;
    const ids = activeSelectedProducts
      .filter((p) => Number(p.destacado) !== target)
      .map((p) => p.producto_id);

    if (ids.length === 0) return;

    setBulkBusy(true);
    setBulkMsg(null);
    try {
      const { ok, fail } = await cambiarDestacadoProductosMasivo(ids, target);
      await cargarProductos();
      setSelectedIds(new Set());
      const accion = target === 1 ? "marcados como destacados" : "quitados de destacados";
      setBulkMsg({
        type: fail ? "error" : "success",
        text: fail
          ? `${ok} producto(s) ${accion}, ${fail} con error.`
          : `${ok} producto(s) ${accion} correctamente.`,
      });
    } catch (e) {
      console.error("Error en acción masiva de destacado:", e);
      setBulkMsg({ type: "error", text: "No se pudo completar la acción masiva." });
    } finally {
      setBulkBusy(false);
    }
  };

  const hasPrev = page > 1;
  const hasNext = page < totalPages;

  const visiblePages = useMemo(() => {
    const total = totalPages;
    if (total <= VISIBLE_PAGE_COUNT) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    let startPage = Math.max(1, page - Math.floor(VISIBLE_PAGE_COUNT / 2));
    let endPage = startPage + VISIBLE_PAGE_COUNT - 1;
    if (endPage > total) {
      endPage = total;
      startPage = endPage - VISIBLE_PAGE_COUNT + 1;
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }, [page, totalPages]);

  const goToPage = (pageNumber) => {
    if (pageNumber === page || pageNumber < 1 || pageNumber > totalPages) return;
    setPage(pageNumber);
  };

  const nextPage = () => hasNext && setPage(page + 1);
  const prevPage = () => hasPrev && setPage(page - 1);

  const openCreateModal = () => {
    navigate("/administracion/tienda/products/nuevo");
  };

  const openEditModal = (product) => {
    navigate(`/administracion/tienda/products/${product.producto_id}/editar`);
  };

  const openLoadInventory = (product) => {
    setSelectedProduct(product);
    setShowLoadInventory(true);
  };

  const openAdjustInventory = (product) => {
    setSelectedProduct(product);
    setShowAdjustInventory(true);
  };

  const closeBulkModal = () => {
    setBulkModalOpen(false);
    setBulkResult(null);
    setBulkError(null);
  };

  const processBulkCsvFile = async (file) => {
    setBulkError(null);
    setBulkResult(null);
    setBulkLoading(true);
    try {
      const data = await crearProductoMasivo(file);
      if (data?.ok) {
        setBulkResult(data);
        setBulkModalView("result");
        setBulkModalOpen(true);
        await cargarProductos();
      } else if (Array.isArray(data?.failed) && data.failed.length > 0) {
        setBulkResult(data);
        setBulkModalView("result");
        setBulkModalOpen(true);
      } else {
        setBulkError(data?.message || "No se pudo completar la carga.");
        setBulkModalView("error");
        setBulkModalOpen(true);
      }
    } catch (err) {
      const payload = err?.data ?? err?.response?.data;
      if (
        payload &&
        typeof payload === "object" &&
        Array.isArray(payload.failed) &&
        payload.failed.length > 0
      ) {
        setBulkResult(payload);
        setBulkModalView("result");
        setBulkModalOpen(true);
      } else {
        const msg =
          payload?.message ||
          err?.response?.data?.message ||
          err?.message ||
          "Error al subir el archivo. Verifica que sea CSV o Excel y que coincida con la plantilla.";
        setBulkError(String(msg));
        setBulkModalView("error");
        setBulkModalOpen(true);
      }
    } finally {
      setBulkLoading(false);
    }
  };

  const onBulkCsvInputChange = async (e) => {
    const file = e.target?.files?.[0];
    e.target.value = "";
    if (!file) return;
    await processBulkCsvFile(file);
  };

  return (
    <div className="tienda-page-scope products-page">
      <div className="header">
        <div className="products-header__row">
          <h2 className="title">Gestión de Productos</h2>

          {hasPerm("create_product") ? (
            <div className="products-header__actions">
              <button type="button" className="products-btn-create" onClick={openCreateModal}>
                Crear Producto
              </button>
              <input
                ref={bulkCsvInputRef}
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                style={{ display: "none" }}
                onChange={onBulkCsvInputChange}
              />
              <div className="import-menu-wrap" ref={importMenuRef}>
                <button
                  type="button"
                  className="btn-import-menu-trigger"
                  aria-label="Importar productos y plantilla"
                  aria-expanded={importMenuOpen}
                  aria-haspopup="menu"
                  onClick={() => setImportMenuOpen((o) => !o)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <circle cx="12" cy="6" r="1.75" />
                    <circle cx="12" cy="12" r="1.75" />
                    <circle cx="12" cy="18" r="1.75" />
                  </svg>
                </button>
                {importMenuOpen && (
                  <div className="import-menu-dropdown" role="menu">
                    <button
                      type="button"
                      role="menuitem"
                      className="import-menu-item"
                      disabled={bulkLoading}
                      onClick={() => {
                        setImportMenuOpen(false);
                        bulkCsvInputRef.current?.click();
                      }}
                    >
                      <span className="import-menu-item__icon" aria-hidden="true">
                        <IconImportUpload />
                      </span>
                      <span className="import-menu-item__text">
                        {bulkLoading ? "Procesando…" : "Importar productos"}
                      </span>
                    </button>
                    <a
                      role="menuitem"
                      className="import-menu-item import-menu-item-link"
                      href={plantillaCsvHref}
                      download="plantilla_productos_carga_masiva.csv"
                      onClick={() => setImportMenuOpen(false)}
                    >
                      <span className="import-menu-item__icon" aria-hidden="true">
                        <IconImportTemplate />
                      </span>
                      <span className="import-menu-item__text">
                        Plantilla para importar productos
                      </span>
                    </a>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        <div className="products-stats" aria-label="Resumen de productos">
          <div className="products-stats__header">
            <span className="products-stats__title">Resumen</span>
            <span className="products-stats__total">
              {productStats.total} producto{productStats.total === 1 ? "" : "s"} en total
            </span>
          </div>
          <div className="products-stats__row">
            <div className="products-stat is-active">
              <span className="products-stat__count">{productStats.activos}</span>
              <span className="products-stat__label">Activos</span>
            </div>
            <div className="products-stat is-inactive">
              <span className="products-stat__count">{productStats.inactivos}</span>
              <span className="products-stat__label">Inactivos</span>
            </div>
            <div className="products-stat is-nostock">
              <span className="products-stat__count">{productStats.sinStock}</span>
              <span className="products-stat__label">Sin stock</span>
            </div>
          </div>
        </div>

        <ProductStatusFilterBar value={statusFilter} onChange={setStatusFilter} />

        <div className="products-filters">
          <div className="products-filters__group products-filters__group--search">
            <label className="products-filters__label" htmlFor="products-search">
              Buscar
            </label>
            <div className="products-filters__search-wrap">
              <input
                id="products-search"
                type="text"
                placeholder="Nombre o SKU…"
                className="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search.trim() ? (
                <button
                  type="button"
                  className="products-filters__clear"
                  onClick={() => setSearch("")}
                  aria-label="Limpiar búsqueda"
                >
                  ×
                </button>
              ) : null}
            </div>
          </div>

          <ProductsFilterDropdown
            id="products-categoria"
            label="Categoría"
            value={categoriaId}
            options={categoriaOptions}
            isOpen={openFilterDropdown === "categoria"}
            onToggle={(e) => {
              e.stopPropagation();
              setOpenFilterDropdown((prev) => (prev === "categoria" ? null : "categoria"));
            }}
            onChange={(value) => {
              setCategoriaId(String(value));
              setOpenFilterDropdown(null);
            }}
          />

          <ProductsFilterDropdown
            id="products-marca"
            label="Marca"
            value={marcaId}
            options={marcaOptions}
            disabled={marcas.length === 0}
            isOpen={openFilterDropdown === "marca"}
            onToggle={(e) => {
              e.stopPropagation();
              setOpenFilterDropdown((prev) => (prev === "marca" ? null : "marca"));
            }}
            onChange={(value) => {
              setMarcaId(String(value));
              setOpenFilterDropdown(null);
            }}
          />

          {hasActiveFilters ? (
            <button type="button" className="products-filters__reset" onClick={clearFilters}>
              Limpiar filtros
            </button>
          ) : null}
        </div>
      </div>

      {hasPerm("update_product") && (selectedIds.size > 0 || bulkMsg) && (
        <div className="products-bulkbar">
          <div className="products-bulkbar__info">
            <strong>{selectedIds.size}</strong> seleccionado
            {selectedIds.size === 1 ? "" : "s"}
          </div>

          <div className="products-bulkbar__actions">
            {hasInactiveSelected && (
              <button
                type="button"
                className="products-bulk-btn products-bulk-btn--activate"
                disabled={bulkBusy}
                onClick={() => handleBulkEstado(1)}
              >
                Activar
              </button>
            )}
            {hasActiveSelected && (
              <button
                type="button"
                className="products-bulk-btn products-bulk-btn--deactivate"
                disabled={bulkBusy}
                onClick={() => handleBulkEstado(0)}
              >
                Inactivar
              </button>
            )}
            {hasActiveSelected && (
              <button
                type="button"
                className={`products-bulk-btn products-bulk-btn--destacar${
                  allActiveSelectedDestacados ? " is-on" : ""
                }`}
                disabled={bulkBusy}
                onClick={handleBulkDestacado}
                title={
                  allActiveSelectedDestacados
                    ? "Quitar de destacados (solo productos activos)"
                    : "Marcar como destacados (solo productos activos)"
                }
              >
                <IconEye off={allActiveSelectedDestacados} />
                <span>
                  {allActiveSelectedDestacados ? "Quitar destacado" : "Destacar"}
                </span>
              </button>
            )}
          </div>

          {bulkBusy ? (
            <span className="products-bulkbar__state">Aplicando…</span>
          ) : bulkMsg ? (
            <span className={`products-bulkbar__state is-${bulkMsg.type}`}>
              {bulkMsg.text}
            </span>
          ) : null}
        </div>
      )}

      <div className="table-card">
        <div className="table-card-scroll">
          <table className="modern-table products-table tienda-table--cards">
            <thead>
              <tr>
                {hasPerm("update_product") && (
                  <th className="products-col-select">
                    <input
                      type="checkbox"
                      aria-label="Seleccionar todos los productos de la página"
                      checked={allPageSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = !allPageSelected && somePageSelected;
                      }}
                      onChange={toggleSelectAllPage}
                    />
                  </th>
                )}
                <th className="products-col-img" aria-label="Imagen" />
                <th>Nombre</th>
                <th className="products-col-modelo">Modelo</th>
                <th className="products-col-categoria">Categoría</th>
                <th className="products-col-marca">Marca</th>
                <th>Disponible</th>
                <th className="products-col-vendido">Vendido</th>
                <th>Precio</th>
                <th>Estado</th>
                <th className="products-col-actions" style={{ textAlign: "center" }}>
                  Acciones
                </th>
              </tr>
            </thead>

            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td
                    colSpan={hasPerm("update_product") ? 11 : 10}
                    className="no-data"
                  >
                    No se encontraron productos
                  </td>
                </tr>
              ) : (
                paginated.map((p) => {
                  const imgSrc = p.url_image ? fileURL(p.url_image) : null;
                  const isSelected = selectedIds.has(p.producto_id);

                  return (
                  <tr
                    key={p.producto_id}
                    className={`products-table__row${isSelected ? " is-selected" : ""}`}
                  >
                    {hasPerm("update_product") && (
                      <td className="products-cell-select" data-label="Seleccionar">
                        <input
                          type="checkbox"
                          aria-label={`Seleccionar ${p.nombre || "producto"}`}
                          checked={isSelected}
                          onChange={() => toggleSelect(p.producto_id)}
                        />
                      </td>
                    )}
                    <td className="products-cell-img" data-label="Imagen">
                      <div className="products-thumb">
                        {imgSrc ? (
                          <img
                            src={imgSrc}
                            alt={p.nombre || "Producto"}
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                              const fb = e.currentTarget.parentElement?.querySelector(
                                ".products-thumb__fallback"
                              );
                              if (fb) fb.style.display = "flex";
                            }}
                          />
                        ) : null}
                        <div
                          className="products-thumb__fallback"
                          style={{ display: imgSrc ? "none" : "flex" }}
                          aria-hidden
                        >
                          —
                        </div>
                      </div>
                    </td>
                    <td data-label="Nombre">
                      <div className="products-name-cell">
                        <span className="products-name-cell__title">{p.nombre}</span>
                        {getProductSku(p) ? (
                          <span className="products-name-cell__sku">SKU {getProductSku(p)}</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="products-col-modelo" data-label="Modelo">
                      {p.modelo}
                    </td>
                    <td className="products-col-categoria" data-label="Categoría">
                      {p.categoria_nombre}
                    </td>
                    <td className="products-col-marca" data-label="Marca">
                      {p.marca_nombre || "-"}
                    </td>

                    <td data-label="Disponible">
                      <strong
                        style={{
                          color:
                            p.cantidad_disponible === 0
                              ? "#b91c1c"
                              : p.cantidad_disponible < 5
                              ? "#d97706"
                              : "#15803d",
                        }}
                      >
                        {p.cantidad_disponible}
                      </strong>
                    </td>

                    <td className="products-col-vendido" data-label="Vendido">
                      <span style={{ color: "#64748b" }}>{p.cantidad_vendida}</span>
                    </td>

                    <td data-label="Precio">
                      {p.en_oferta ? (
                        <div className="products-price-cell">
                          <div className="products-price-cell__row">
                            <span className="products-price-cell__new">
                              {moneyQ(p.precio_efectivo)}
                            </span>
                            <span className="products-price-cell__badge">
                              −{p.descuento_porcentaje}%
                            </span>
                          </div>
                          <span className="products-price-cell__old">
                            {moneyQ(p.precio_unitario)}
                          </span>
                        </div>
                      ) : (
                        moneyQ(p.precio_unitario)
                      )}
                    </td>

                    <td data-label="Estado">
                      <span className={p.estado === 1 ? "status active" : "status inactive"}>
                        {p.estado === 1 ? "Activo" : "Inactivo"}
                      </span>
                    </td>

                    <td className="products-col-actions" data-label="Acciones">
                      <div className="products-row-actions">
                        {hasPerm("update_product") && (
                          <span
                            className="action-icon edit"
                            onClick={() => openEditModal(p)}
                            title="Editar producto"
                          >
                            {IconEdit}
                          </span>
                        )}

                        {hasPerm("add_inventory_product") && (
                          <span
                            className="action-icon"
                            onClick={() => openLoadInventory(p)}
                            title="Agregar inventario"
                            style={{ background: "#ECFEFF" }}
                          >
                            {IconPlus}
                          </span>
                        )}

                        {hasPerm("adjust_inventory_product") && (
                          <span
                            className="action-icon"
                            onClick={() => openAdjustInventory(p)}
                            title="Ajustar inventario"
                            style={{ background: "#FFF7ED" }}
                          >
                            {IconSettings}
                          </span>
                        )}

                        {hasPerm("inventory_history_product") && (
                          <span
                            className="action-icon"
                            onClick={() => {
                              setSelectedProduct(p);
                              setShowHistory(true);
                            }}
                            title="Historial de inventario"
                            style={{ background: "#EEF2FF" }}
                          >
                            {IconHistory}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {filtered.length > 0 && (
          <div className="pagination">
            <div className="pagination__center">
              <div className="pagination__nav" aria-label="Paginación de productos">
                <button
                  type="button"
                  className="pagination__arrow"
                  onClick={prevPage}
                  disabled={!hasPrev}
                  aria-label="Página anterior"
                >
                  ‹
                </button>

                <div className="pagination__pages">
                  {visiblePages.map((pageNumber) => (
                    <button
                      key={pageNumber}
                      type="button"
                      className={`pagination__page${
                        pageNumber === page ? " is-active" : ""
                      }`}
                      onClick={() => goToPage(pageNumber)}
                      aria-label={`Ir a página ${pageNumber}`}
                      aria-current={pageNumber === page ? "page" : undefined}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  className="pagination__arrow"
                  onClick={nextPage}
                  disabled={!hasNext}
                  aria-label="Página siguiente"
                >
                  ›
                </button>
              </div>

              <div className="pagination__size">
                <span className="pagination__size-label">Mostrar</span>
                <select
                  className="page-size-select"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  title="Filas por página"
                  aria-label="Filas por página"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <span className="pagination__size-label">por página</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {showLoadInventory && (
        <InventoryLoadModal
          isOpen={showLoadInventory}
          product={selectedProduct}
          onClose={() => setShowLoadInventory(false)}
          onSuccess={cargarProductos}
        />
      )}

      {showAdjustInventory && (
        <InventoryAdjustModal
          isOpen={showAdjustInventory}
          product={selectedProduct}
          onClose={() => setShowAdjustInventory(false)}
          onSuccess={cargarProductos}
        />
      )}

      {showHistory && (
        <InventoryHistoryModal
          isOpen={showHistory}
          product={selectedProduct}
          onClose={() => setShowHistory(false)}
        />
      )}

      <ModalResultadoCargaMasiva
        isOpen={bulkModalOpen}
        view={bulkModalView}
        result={bulkResult}
        errorMessage={bulkError}
        onClose={closeBulkModal}
      />
    </div>
  );
}

function IconImportUpload() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 16V4" />
      <path d="M8 8l4-4 4 4" />
      <path d="M4 18v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </svg>
  );
}

function IconEye({ off = false }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
      {off ? <path d="M3 3l18 18" /> : null}
    </svg>
  );
}

function IconImportTemplate() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M12 18v-6" />
      <path d="M9 15l3 3 3-3" />
    </svg>
  );
}

function ProductStatusFilterBar({ value, onChange }) {
  return (
    <div className="products-status-filter" role="group" aria-label="Filtrar productos por estado">
      <span className="products-status-filter__label">Ver</span>
      <div className="products-status-filter__options">
        {PRODUCT_STATUS_FILTER_OPTIONS.map((opt) => {
          const toneClass = opt.tone && opt.tone !== "all" ? ` is-${opt.tone}` : "";
          return (
            <button
              key={opt.key}
              type="button"
              className={`products-status-filter__btn${
                value === opt.key ? " is-active" : ""
              }${toneClass}`}
              aria-pressed={value === opt.key}
              onClick={() => onChange(opt.key)}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ProductsFilterDropdown({
  id,
  label,
  value,
  options,
  isOpen,
  onToggle,
  onChange,
  disabled = false,
}) {
  const triggerRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  const selectedLabel =
    options.find((opt) => String(opt.value) === String(value))?.label ?? "—";

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) {
      setMenuStyle(null);
      return;
    }

    const updateMenuPosition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < FILTER_MENU_MAX_HEIGHT + 12;

      setMenuStyle({
        position: "fixed",
        top: openUp ? rect.top - FILTER_MENU_MAX_HEIGHT - 6 : rect.bottom + 6,
        left: rect.left,
        minWidth: Math.max(rect.width, 200),
        maxHeight: FILTER_MENU_MAX_HEIGHT,
        zIndex: 10050,
      });
    };

    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen]);

  return (
    <div
      className={`products-filters__group products-filter-dropdown${
        isOpen ? " is-open" : ""
      }${disabled ? " is-disabled" : ""}`}
    >
      <span className="products-filters__label" id={`${id}-label`}>
        {label}
      </span>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        className="products-filter-dropdown__trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={`${id}-label`}
        disabled={disabled}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={onToggle}
      >
        <span className="products-filter-dropdown__value">{selectedLabel}</span>
        <span className="products-filter-dropdown__chevron" aria-hidden="true" />
      </button>

      {isOpen &&
        !disabled &&
        menuStyle &&
        createPortal(
          <div
            className="products-filter-dropdown__menu products-filter-dropdown__menu--portal"
            style={menuStyle}
            role="listbox"
            aria-labelledby={`${id}-label`}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {options.map((opt) => {
              const selected = String(opt.value) === String(value);
              return (
                <button
                  key={String(opt.value) || "__all__"}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`products-filter-dropdown__option${
                    selected ? " is-selected" : ""
                  }`}
                  onClick={() => onChange(opt.value)}
                >
                  <span className="products-filter-dropdown__option-text">
                    {opt.label}
                  </span>
                  {selected ? (
                    <span className="products-filter-dropdown__option-check" aria-hidden="true">
                      ✓
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
}

export default ProductsPage;
